import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link2, Plus, Trash2, Copy, Check, ExternalLink, BarChart3, X, Loader2, MousePointerClick, Calendar, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const ShortLinks = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);
  const [analyticsId, setAnalyticsId] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [form, setForm] = useState({ url: '', title: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ url: '', title: '' });
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => { fetchLinks(); }, []);

  const fetchLinks = async () => {
    try { const res = await axios.get(`${API}/short-links`); setLinks(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.url.trim()) { toast.error('URL is required'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/short-links`, form);
      setLinks([res.data, ...links]);
      setForm({ url: '', title: '' });
      setShowForm(false);
      toast.success('Short link created!');
    } catch (e) { toast.error('Failed to create'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/short-links/${id}`);
      setLinks(links.filter(l => l.id !== id));
      if (analyticsId === id) { setAnalyticsId(null); setAnalyticsData(null); }
      toast.success('Link deleted');
    } catch (e) { toast.error('Failed to delete'); }
  };

  const startEdit = (l) => {
    setEditingId(l.id);
    setEditForm({ url: l.url, title: l.title });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ url: '', title: '' });
  };

  const handleUpdate = async () => {
    if (!editForm.url.trim()) { toast.error('URL is required'); return; }
    setEditSaving(true);
    try {
      const res = await axios.put(`${API}/short-links/${editingId}`, editForm);
      setLinks(links.map(l => l.id === editingId ? res.data : l));
      setEditingId(null);
      toast.success('Link updated!');
    } catch (e) { toast.error('Failed to update'); }
    finally { setEditSaving(false); }
  };

  const getShortUrl = (l) => `${BASE}/api/s/${l.code}`;
  const getDisplayUrl = (l) => `createonelink.com/${l.code}`;

  const copyLink = async (l) => {
    try {
      await navigator.clipboard.writeText(getDisplayUrl(l));
      setCopied(l.id);
      toast.success('Short link copied!');
      setTimeout(() => setCopied(null), 2000);
    } catch { toast.error('Failed to copy'); }
  };

  const viewAnalytics = async (id) => {
    if (analyticsId === id) { setAnalyticsId(null); setAnalyticsData(null); return; }
    setAnalyticsId(id);
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/short-links/${id}/analytics`);
      setAnalyticsData(res.data);
    } catch { toast.error('Failed to load analytics'); }
    finally { setAnalyticsLoading(false); }
  };

  return (
    <div className="max-w-4xl" data-testid="short-links-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">Short Links</h1>
          <p className="text-[#A1A1AA] mt-1 text-sm">Shorten any URL and track clicks</p>
        </div>
        <Button data-testid="create-short-link-btn" onClick={() => setShowForm(true)}
          className="bg-violet-600 hover:bg-violet-700 rounded-xl">
          <Plus className="w-4 h-4 mr-1" /> Shorten URL
        </Button>
      </div>

      {showForm && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-[#121212] rounded-2xl border border-[#27272A] p-5 mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-white font-semibold">Shorten a URL</h2>
            <button onClick={() => setShowForm(false)}><X className="w-5 h-5 text-[#71717A]" /></button>
          </div>
          <div>
            <Label className="text-[#A1A1AA] text-xs">Destination URL *</Label>
            <Input data-testid="short-link-url" value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))}
              className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="https://example.com/your-long-url" />
          </div>
          <div>
            <Label className="text-[#A1A1AA] text-xs">Title (optional)</Label>
            <Input data-testid="short-link-title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="My Marketing Link" />
          </div>
          <Button data-testid="save-short-link" onClick={handleCreate} disabled={saving}
            className="w-full rounded-xl bg-violet-600 hover:bg-violet-700 h-10">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
            Create Short Link
          </Button>
        </motion.div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : links.length === 0 && !showForm ? (
        <div className="text-center py-20 bg-[#121212] rounded-2xl border border-[#27272A]">
          <Link2 className="w-12 h-12 text-[#525252] mx-auto mb-4" />
          <h3 className="text-white font-semibold text-lg mb-2">No short links yet</h3>
          <p className="text-[#71717A] text-sm mb-4">Shorten any URL and track how many times it's clicked</p>
          <Button onClick={() => setShowForm(true)} className="bg-violet-600 hover:bg-violet-700 rounded-xl">
            <Plus className="w-4 h-4 mr-1" /> Shorten URL
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {links.map((l, i) => (
            <motion.div key={l.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}>
              <div className="rounded-xl bg-[#121212] border border-[#27272A] p-4" data-testid={`short-link-${i}`}>
                {editingId === l.id ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-white font-medium text-sm">Edit Link</p>
                      <button onClick={cancelEdit}><X className="w-4 h-4 text-[#71717A]" /></button>
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA] text-xs">URL</Label>
                      <Input data-testid={`edit-url-${i}`} value={editForm.url} onChange={e => setEditForm(p => ({ ...p, url: e.target.value }))}
                        className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white text-sm" />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA] text-xs">Title</Label>
                      <Input data-testid={`edit-title-${i}`} value={editForm.title} onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))}
                        className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white text-sm" />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={cancelEdit} className="flex-1 py-2 rounded-lg border border-[#27272A] text-[#A1A1AA] text-xs hover:bg-white/5">Cancel</button>
                      <Button data-testid={`save-edit-${i}`} onClick={handleUpdate} disabled={editSaving}
                        className="flex-1 rounded-lg bg-violet-600 hover:bg-violet-700 h-9 text-xs">
                        {editSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                      </Button>
                    </div>
                    <p className="text-[#525252] text-[10px]">Short code stays the same: {getDisplayUrl(l)}</p>
                  </div>
                ) : (
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center flex-shrink-0">
                    <Link2 className="w-5 h-5 text-violet-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium text-sm truncate">{l.title}</h3>
                    <p className="text-violet-400 text-xs truncate mt-0.5" data-testid={`short-url-${i}`}>{getDisplayUrl(l)}</p>
                    <p className="text-[#525252] text-[10px] truncate mt-0.5">{l.url}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-center">
                      <p className="text-white font-semibold text-sm">{l.clicks}</p>
                      <p className="text-[#525252] text-[10px]">clicks</p>
                    </div>
                    <button onClick={() => copyLink(l)} className="p-2 rounded-lg hover:bg-white/5" title="Copy short link">
                      {copied === l.id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-[#A1A1AA]" />}
                    </button>
                    <button data-testid={`edit-btn-${i}`} onClick={() => startEdit(l)} className="p-2 rounded-lg hover:bg-white/5" title="Edit">
                      <Edit2 className="w-4 h-4 text-[#A1A1AA]" />
                    </button>
                    <button data-testid={`analytics-btn-${i}`} onClick={() => viewAnalytics(l.id)} className="p-2 rounded-lg hover:bg-white/5" title="Analytics">
                      <BarChart3 className={`w-4 h-4 ${analyticsId === l.id ? 'text-violet-400' : 'text-[#A1A1AA]'}`} />
                    </button>
                    <a href={l.url} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-white/5">
                      <ExternalLink className="w-4 h-4 text-[#A1A1AA]" />
                    </a>
                    <button onClick={() => handleDelete(l.id)} className="p-2 rounded-lg hover:bg-red-500/10" title="Delete">
                      <Trash2 className="w-4 h-4 text-red-400/60" />
                    </button>
                  </div>
                </div>
                )}
              </div>
              {/* Analytics Panel */}
              {analyticsId === l.id && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                  className="bg-[#0A0A0A] border border-[#27272A] border-t-0 rounded-b-xl p-4" data-testid={`analytics-panel-${i}`}>
                  {analyticsLoading ? (
                    <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 text-violet-400 animate-spin" /></div>
                  ) : analyticsData ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-[#121212] rounded-lg p-3 text-center">
                          <MousePointerClick className="w-5 h-5 text-violet-400 mx-auto mb-1" />
                          <p className="text-white font-bold text-lg">{analyticsData.total_clicks}</p>
                          <p className="text-[#71717A] text-[10px]">Total Clicks</p>
                        </div>
                        <div className="bg-[#121212] rounded-lg p-3 text-center">
                          <Calendar className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                          <p className="text-white font-bold text-lg">
                            {analyticsData.daily.length > 0 ? analyticsData.daily[analyticsData.daily.length - 1].clicks : 0}
                          </p>
                          <p className="text-[#71717A] text-[10px]">Today</p>
                        </div>
                      </div>
                      {analyticsData.daily.length > 0 && (
                        <div>
                          <p className="text-[#A1A1AA] text-xs mb-2">Click History</p>
                          <div className="flex items-end gap-1 h-16">
                            {analyticsData.daily.slice(-14).map((d, idx) => {
                              const max = Math.max(...analyticsData.daily.slice(-14).map(x => x.clicks));
                              const h = max > 0 ? (d.clicks / max) * 100 : 0;
                              return (
                                <div key={idx} className="flex-1 group relative">
                                  <div className="bg-violet-500/60 rounded-t-sm transition-all group-hover:bg-violet-400"
                                    style={{ height: `${Math.max(h, 4)}%` }} />
                                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-[#1E1E1E] px-1.5 py-0.5 rounded text-[9px] text-white opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none">
                                    {d.clicks} • {d.date.slice(5)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {analyticsData.recent_clicks.length > 0 && (
                        <div>
                          <p className="text-[#A1A1AA] text-xs mb-2">Recent Clicks</p>
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {analyticsData.recent_clicks.slice(0, 10).map((c, idx) => (
                              <div key={idx} className="flex justify-between text-[10px] text-[#71717A]">
                                <span>{new Date(c.clicked_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                <span className="truncate ml-4 max-w-[200px]">{c.referer || 'Direct'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ShortLinks;
